/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useSchedule.ts
 * PURPOSE: Custom React hooks for state management
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (React Best Practices)
 * SPRINT GOVERNANCE: Rule 9: Documentation Standards
 *
 *
 * @business-context
 * - Domain: React Hooks, State Management, Vietnamese F&B Business Logic
 * - Impact: Code Reusability, State Consistency, Developer Productivity
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: Business Logic Layer, Custom Hooks
 * - Pattern: React Hooks Pattern, State Management, API Integration
 * - Dependencies: React 18, TypeScript, API Services
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/FRONTEND-BACKEND-GAP-ANALYSIS-SPRINT10-13.md
 *  */

import { useState, useEffect, useCallback } from "react";
import { scheduleApi, Schedule, ScheduleCreate, ShiftSwapRequest } from "../services/api/schedule";
import { useAuthStore } from "../store/auth";

interface UseScheduleReturn {
  schedules: Schedule[];
  loading: boolean;
  error: string | null;
  refreshSchedules: () => Promise<void>;
  createSchedule: (data: ScheduleCreate) => Promise<void>;
  updateSchedule: (id: string, data: Partial<Schedule>) => Promise<void>;
  requestSwap: (data: ShiftSwapRequest) => Promise<void>;
  publishSchedule: (id: string) => Promise<void>;
  checkConflicts: (userId: string, date: string) => Promise<any>;
  optimizeSchedule: (date: string) => Promise<any>;
}

export const useSchedule = (startDate?: string, endDate?: string): UseScheduleReturn => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuthStore();

  const fetchSchedules = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const data = await scheduleApi.getMySchedule(accessToken, startDate, endDate);
      setSchedules(data);
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_fetch_sche"));
      // Removed console.log, err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, startDate, endDate]);

  const createSchedule = async (data: ScheduleCreate) => {
    if (!accessToken) throw new Error(t("hooks.useSchedule.not_authenticated"));

    setLoading(true);
    setError(null);

    try {
      const newSchedule = await scheduleApi.createSchedule(accessToken, data);
      setSchedules(prev => [...prev, newSchedule]);
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_create_sch"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateSchedule = async (id: string, data: Partial<Schedule>) => {
    if (!accessToken) throw new Error(t("hooks.useSchedule.not_authenticated"));

    setLoading(true);
    setError(null);

    try {
      const updated = await scheduleApi.updateSchedule(accessToken, id, data);
      setSchedules(prev => prev.map(s => s.id === id ? updated : s));
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_update_sch"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const requestSwap = async (data: ShiftSwapRequest) => {
    if (!accessToken) throw new Error(t("hooks.useSchedule.not_authenticated"));

    setLoading(true);
    setError(null);

    try {
      await scheduleApi.requestSwap(accessToken, data);
      // Refresh schedules after swap request
      await fetchSchedules();
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_request_sw"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const publishSchedule = async (id: string) => {
    if (!accessToken) throw new Error(t("hooks.useSchedule.not_authenticated"));

    setLoading(true);
    setError(null);

    try {
      const published = await scheduleApi.publishSchedule(accessToken, id);
      setSchedules(prev => prev.map(s => s.id === id ? published : s));
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_publish_sc"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const checkConflicts = async (userId: string, date: string) => {
    if (!accessToken) throw new Error(t("hooks.useSchedule.not_authenticated"));

    return scheduleApi.checkConflicts(accessToken, userId, date);
  };

  const optimizeSchedule = async (date: string) => {
    if (!accessToken) throw new Error(t("hooks.useSchedule.not_authenticated"));

    return scheduleApi.optimizeSchedule(accessToken, date);
  };

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return {
    schedules,
    loading,
    error,
    refreshSchedules: fetchSchedules,
    createSchedule,
    updateSchedule,
    requestSwap,
    publishSchedule,
    checkConflicts,
    optimizeSchedule
  };
};

/**
 * Hook for manager schedule operations
 */
export const useManagerSchedule = (filters?: any) => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuthStore();

  const fetchAllSchedules = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const data = await scheduleApi.getAllSchedules(accessToken, filters);
      setSchedules(data);
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_fetch_all"));
      // Removed console.log, err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, filters]);

  const bulkCreateSchedules = async (schedules: ScheduleCreate[]) => {
    if (!accessToken) throw new Error(t("hooks.useSchedule.not_authenticated"));

    setLoading(true);
    setError(null);

    try {
      const created = await scheduleApi.bulkCreateSchedules(accessToken, schedules);
      setSchedules(prev => [...prev, ...created]);
      return created;
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_bulk_creat"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllSchedules();
  }, [fetchAllSchedules]);

  return {
    schedules,
    loading,
    error,
    refreshSchedules: fetchAllSchedules,
    bulkCreateSchedules
  };
};

/**
 * Hook for shift templates
 */
export const useShiftTemplates = () => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuthStore();

  const fetchTemplates = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    setError(null);

    try {
      const data = await scheduleApi.getShiftTemplates(accessToken);
      setTemplates(data);
    } catch (err: any) {
      setError(err.message || t("hooks.useSchedule.failed_to_fetch_generic"));
      // Removed console.log, err);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    loading,
    error,
    refreshTemplates: fetchTemplates
  };
};